import React, { useCallback, useEffect, useState } from "react";
import { ClipboardList, Loader2, Image as ImageIcon, CheckCircle2, PlayCircle, UserCheck, PencilLine, FilePlus2, History } from "lucide-react";
import { api, fmtDate, fmtDateTime } from "@/lib/apiClient";
import { DeskripsiEditor } from "@/components/DeskripsiEditor";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const stamp = (iso) =>
  iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "";

export const ProductionBoard = ({ role }) => {
  const [date, setDate] = useState("");
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [clientQ, setClientQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [acts, setActs] = useState([]);

  const load = useCallback(async () => {
    const params = {};
    if (date) params.date = date;
    if (statusFilter !== "ALL") params.status = statusFilter;
    const [b, a] = await Promise.all([
      api.get("/production-board", { params }),
      api.get("/activities"),
    ]);
    setRows(b.data);
    setActs(
      a.data
        .filter((x) => x.action !== "LOGIN")
        .slice(0, 100)
    );
  }, [date, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (jo, kategori, status) => {
    setBusy(`${jo.jo_id}-${kategori}`);
    try {
      await api.put(`/job-orders/${jo.jo_id}/items/${encodeURIComponent(kategori)}`, { status });
      toast.success(`${jo.jo_number} · ${kategori} → ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal update status");
    } finally {
      setBusy(null);
    }
  };

  const viewPhoto = async (fileId) => {
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Gagal membuka foto");
    }
  };

  const cariClient = clientQ.trim().toLowerCase();
  const view = cariClient
    ? rows.filter((jo) => (jo.client_name || "").toLowerCase().includes(cariClient))
    : rows;

  const openCount = view.reduce(
    (n, jo) => n + jo.items.filter((i) => ["MASUK", "ON PROSES"].includes(i.status)).length,
    0
  );

  return (
    <div className="space-y-6" data-testid="production-board">
      <div className="glass rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-blue-400" />
            <div>
              <h3 className="font-display text-xl font-semibold">Papan Produksi</h3>
              <p className="text-xs text-slate-400 mt-1">
                Antrean per Job Order ·{" "}
                <span className="text-blue-300 font-semibold" data-testid="board-total">
                  {view.length} JO / {openCount} kategori belum selesai
                </span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              data-testid="board-client-filter"
              placeholder="Filter nama client"
              value={clientQ}
              onChange={(e) => setClientQ(e.target.value)}
              className="h-10 w-52 bg-slate-900/70 border-white/10"
            />
            <select
              data-testid="board-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">Semua Antrean</option>
              <option value="MASUK">MASUK</option>
              <option value="ON PROSES">ON PROSES</option>
            </select>
            <span className="text-xs uppercase tracking-[0.16em] text-slate-400">Tanggal</span>
            <Input
              data-testid="board-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 w-auto bg-slate-900/70 border-white/10"
            />
            {date && (
              <button
                data-testid="board-clear-date"
                onClick={() => setDate("")}
                className="text-xs text-slate-400 hover:text-blue-300 transition-colors duration-200"
              >
                semua tanggal
              </button>
            )}
          </div>
        </div>
      </div>

      {view.length === 0 && (
        <div className="glass rounded-2xl p-10 text-center text-slate-500" data-testid="board-empty">
          {cariClient
            ? `Tidak ada antrean untuk client "${clientQ}".`
            : "Tidak ada antrean pekerjaan. Semua Job Order sudah selesai."}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {view.map((jo) => {
          const done = jo.items.filter((i) => !["MASUK", "ON PROSES"].includes(i.status)).length;
          return (
            <div
              key={jo.jo_id}
              className="glass rounded-2xl p-5 md:p-6"
              data-testid={`board-jo-${jo.jo_number}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-4">
                <div>
                  <div className="font-display text-lg font-semibold text-slate-100">
                    JO {jo.jo_number}{" "}
                    <span className="text-xs font-normal text-slate-500">· {fmtDate(jo.tanggal)}</span>
                  </div>
                  <div className="text-sm text-slate-300 mt-0.5">{jo.client_name}</div>
                </div>
                <span className="rounded-md border border-white/10 bg-slate-900/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
                  {done}/{jo.items.length} selesai
                </span>
              </div>

              <DeskripsiEditor
                joId={jo.jo_id}
                joNumber={jo.jo_number}
                value={jo.deskripsi}
                onChanged={load}
                testIdPrefix="board-desk"
              />

              <div
                className="mt-4 rounded-xl border border-white/5 bg-slate-950/50 p-3 space-y-1.5"
                data-testid={`board-history-${jo.jo_number}`}
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">
                  Riwayat aktivitas JO
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-300">
                  <FilePlus2 className="w-3.5 h-3.5 text-blue-300 shrink-0" />
                  Entri oleh{" "}
                  <span className="font-semibold text-blue-200">{jo.created_by || "-"}</span>
                  <span className="text-slate-500">{stamp(jo.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-300">
                  <UserCheck className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                  ACC{" "}
                  <span className="font-semibold text-emerald-200">
                    {jo.admin_status || "belum"}
                  </span>
                  {jo.admin_by && (
                    <>
                      oleh <span className="font-semibold text-emerald-200">{jo.admin_by}</span>
                      <span className="text-slate-500">{stamp(jo.admin_at)}</span>
                    </>
                  )}
                </div>
                {(jo.edits || []).map((e) => (
                  <div key={`edit-${e.at}-${e.by}`} className="flex items-start gap-2 text-[11px] text-slate-300">
                    <PencilLine className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
                    <span>
                      Diedit oleh{" "}
                      <span className="font-semibold text-amber-200">{e.by}</span>{" "}
                      <span className="text-slate-500">{stamp(e.at)}</span>
                      {e.fields?.length > 0 && (
                        <span className="text-slate-500"> · {e.fields.join(", ")}</span>
                      )}
                    </span>
                  </div>
                ))}
                {(jo.item_logs || []).slice(-6).reverse().map((l) => (
                  <div key={`log-${l.at}-${l.kategori}`} className="flex items-start gap-2 text-[11px] text-slate-300">
                    <History className="w-3.5 h-3.5 text-sky-300 shrink-0 mt-0.5" />
                    <span>
                      <span className="font-semibold text-sky-200">{l.kategori}</span> →{" "}
                      <span className="font-semibold text-slate-100">{l.status}</span> oleh{" "}
                      <span className="font-semibold text-sky-200">{l.by}</span>{" "}
                      <span className="text-slate-500">{stamp(l.at)}</span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 space-y-3">
                {jo.items.map((it) => {
                  const k = `${jo.jo_id}-${it.kategori}`;
                  const isBusy = busy === k;
                  const finished = !["MASUK", "ON PROSES"].includes(it.status);
                  return (
                    <div
                      key={it.kategori}
                      data-testid={`board-item-${jo.jo_number}-${it.kategori}`}
                      className={`rounded-xl border p-3.5 transition-colors duration-200 ${
                        finished
                          ? "border-emerald-400/20 bg-emerald-500/5"
                          : "border-white/10 bg-slate-900/70"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                          <span className="rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200">
                            {it.kategori}
                          </span>
                          <span
                            data-testid={`board-item-status-${jo.jo_number}-${it.kategori}`}
                            className={`text-[10px] font-semibold uppercase tracking-wider ${
                              it.status === "SELESAI" || it.status === "DITERIMA"
                                ? "text-emerald-300"
                                : it.status === "ON PROSES"
                                ? "text-blue-300"
                                : "text-amber-300"
                            }`}
                          >
                            {it.status}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            data-testid={`board-btn-onproses-${jo.jo_number}-${it.kategori}`}
                            disabled={isBusy || it.status === "ON PROSES"}
                            onClick={() => setStatus(jo, it.kategori, "ON PROSES")}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-400/30 bg-blue-500/10 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-200 hover:bg-blue-500/20 disabled:opacity-30 transition-colors duration-200"
                          >
                            {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                            On Proses
                          </button>
                          <button
                            data-testid={`board-btn-selesai-${jo.jo_number}-${it.kategori}`}
                            disabled={isBusy || finished}
                            onClick={() => setStatus(jo, it.kategori, "SELESAI")}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-30 transition-colors duration-200"
                          >
                            {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            Selesai
                          </button>
                          {it.photo_file_id && (                            <button
                              data-testid={`board-view-photo-${jo.jo_number}-${it.kategori}`}
                              onClick={() => viewPhoto(it.photo_file_id)}
                              className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 transition-colors duration-200"
                            >
                              <ImageIcon className="w-4 h-4" /> Foto
                            </button>
                          )}
                        </div>
                      </div>
                      <div
                        className="mt-2.5 border-t border-white/5 pt-2 space-y-1"
                        data-testid={`board-item-history-${jo.jo_number}-${it.kategori}`}
                      >
                        {(jo.item_logs || []).filter((l) => l.kategori === it.kategori).length === 0 ? (
                          <div className="text-[10px] text-slate-600">Belum ada riwayat kategori ini</div>
                        ) : (
                          (jo.item_logs || [])
                            .filter((l) => l.kategori === it.kategori)
                            .slice(-5)
                            .reverse()
                            .map((l) => (
                              <div key={`kl-${l.at}-${l.status}`} className="flex items-center gap-2 text-[10px] text-slate-400">
                                <History className="w-3 h-3 text-sky-300 shrink-0" />
                                <span className="font-semibold text-slate-200">{l.status}</span>
                                oleh <span className="font-semibold text-sky-200">{l.by}</span>
                                <span className="text-slate-500">{stamp(l.at)}</span>
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="glass rounded-2xl overflow-hidden" data-testid="board-activity-wrap">
        <div className="p-5 md:p-6 border-b border-white/10 flex items-center gap-3">
          <History className="w-5 h-5 text-blue-400" />
          <h3 className="font-display text-xl font-semibold">
            Riwayat Aktivitas{" "}
            <span className="text-slate-500 text-sm font-normal">({acts.length})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="board-activity-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-slate-400">
                <th className="px-5 py-4 font-semibold">Tanggal &amp; Waktu</th>
                <th className="px-5 py-4 font-semibold">Username</th>
                <th className="px-5 py-4 font-semibold">Aktivitas</th>
                <th className="px-5 py-4 font-semibold">Keterangan</th>
              </tr>
            </thead>
            <tbody>
              {acts.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-slate-500" data-testid="board-activity-empty">
                    Belum ada aktivitas.
                  </td>
                </tr>
              )}
              {acts.map((a) => (
                <tr key={a.id} className="border-t border-white/5">
                  <td className="px-5 py-3.5 text-slate-300 whitespace-nowrap">{fmtDateTime(a.at)}</td>
                  <td className="px-5 py-3.5 text-slate-200">{a.username}</td>
                  <td className="px-5 py-3.5">
                    <span className="rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200">
                      {a.action}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-400">{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
